import React from 'react';
import styled from 'styled-components';
import CalendarIcon from 'assets/calendar';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

interface DateInputProps {
  date: Date | null;
  onClick: () => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  id?: string;
  dateFormat?: string;
}

const DateInput: React.FC<DateInputProps> = ({
  date,
  onClick,
  placeholder,
  label,
  disabled = false,
  id,
  dateFormat = 'MMM D',
}) => {
  const { t: translate } = useTranslation();

  let displayValue = placeholder || '';
  const ariaLabel = label || placeholder || translate('selectDate');

  if (date) {
    displayValue = moment(date).format(dateFormat);
  }

  return (
    <DateInputContainer>
      <InputWrapper
        aria-disabled={disabled}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={disabled ? undefined : onClick}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onClick();
          }
        }}
      >
        <IconWrapper>
          <CalendarIcon />
        </IconWrapper>
        <InputDisplay
          aria-label={ariaLabel}
          disabled={disabled}
          id={id}
          showPlaceholderCss={!date}
        >
          {displayValue}
        </InputDisplay>
      </InputWrapper>
    </DateInputContainer>
  );
};

const DateInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const InputWrapper = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  border: 0.5px solid var(--way-colors-borderColor);
  border-radius: var(--way-design-borderRadiusDefault);
  padding: 0 8px;
  background-color: ${({ disabled }) =>
    disabled ? 'var(--way-palette-black-10)' : 'var(--way-palette-white-100)'};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${({ disabled }) =>
      disabled ? 'var(--way-palette-black-30)' : 'var(--way-palette-black-50)'};
  }

  &:focus-within {
    border-color: var(--way-palette-black-70);
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
`;

// Using data attributes to style in order to keep individual styles for each state
const InputDisplay = styled.div.attrs<{
  disabled?: boolean;
  showPlaceholderCss?: boolean;
}>(({ disabled, showPlaceholderCss }) => ({
  'data-disabled': disabled ? 'true' : 'false',
  'data-show-placeholder': showPlaceholderCss ? 'true' : 'false',
}))<{ disabled?: boolean; showPlaceholderCss?: boolean }>`
  border-bottom: none !important;
  width: 100%;
  height: 100%;
  font: inherit;
  font-size: 16px;
  line-height: 19px;
  padding: 17px 12px;
  border-radius: var(--way-design-borderRadiusDefault);
  background: transparent;
  border: none;
  min-height: 1px;
  font-weight: normal;
  font-style: normal;
  pointer-events: none; /* Prevent any interaction with this element */

  &[data-show-placeholder='true'],
  &[data-disabled='true'] {
    color: var(--way-palette-black-60);
  }

  &[data-show-placeholder='false']:not([data-disabled='true']) {
    color: var(--way-colors-calendarInputColor);
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;

  svg {
    width: 20px;
    height: 20px;

    path,
    rect {
      stroke: var(--way-colors-svgColor);
    }
  }
`;

export default DateInput;
